import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Card, Badge, Button, Form, InputGroup } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useFetch } from '../hooks/useApiCall';
import { getCourses } from '../services/courseService';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import data from '../data/paomakeup';

const modalityLabels = {
  online: { label: 'Online', variant: 'info' },
  presencial: { label: 'Presencial', variant: 'success' },
  hybrid: { label: 'Hibrido', variant: 'warning' },
};

const formatPrice = (price, currency = 'DOP') => {
  if (price === 0 || price === '0') return 'Gratis';
  return new Intl.NumberFormat('es-DO', { style: 'currency', currency }).format(price);
};

const CourseList = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeSearch, setActiveSearch] = useState('');

  const params = useMemo(() => {
    const p = { page: currentPage };
    if (activeSearch) p.search = activeSearch;
    return p;
  }, [currentPage, activeSearch]);

  const { data: response, loading, error, refetch } = useFetch(getCourses, params);

  const courses = response?.data ?? (Array.isArray(response) ? response : []);
  const meta = response?.meta ?? null;
  const totalPages = meta?.last_page ?? 1;

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setActiveSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setActiveSearch('');
    setCurrentPage(1);
  };

  return (
    <Container className="my-5">
      <Helmet>
        <title>Cursos - {data.title}</title>
        <meta name="description" content="Descubre nuestros cursos de belleza y maquillaje. Aprende de la mano de profesionales." />
      </Helmet>

      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="courses-page-title">Nuestros Cursos</h1>
          <p className="text-muted">Aprende y domina el arte del maquillaje con nuestros profesionales.</p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Form onSubmit={handleSearchSubmit}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Buscar cursos..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <Button variant="outline-secondary" type="submit">
                <Search size={16} />
              </Button>
              {activeSearch && (
                <Button variant="outline-danger" type="button" onClick={handleClearSearch}>
                  Limpiar
                </Button>
              )}
            </InputGroup>
          </Form>
        </Col>
      </Row>

      {loading && <LoadingSpinner />}
      {error && <ErrorAlert message={error} onRetry={refetch} />}

      {!loading && !error && courses.length === 0 && (
        <div className="text-center py-5">
          <h4>No se encontraron cursos</h4>
          <p className="text-muted">
            {activeSearch
              ? `No hay cursos que coincidan con "${activeSearch}".`
              : 'Por el momento no hay cursos disponibles. Vuelve pronto.'}
          </p>
          {activeSearch && (
            <Button variant="outline-secondary" onClick={handleClearSearch}>
              Ver todos los cursos
            </Button>
          )}
        </div>
      )}

      {!loading && !error && courses.length > 0 && (
        <>
          <Row>
            {courses.map((course) => {
              const modality = modalityLabels[course.modality] || { label: course.modality, variant: 'secondary' };
              return (
                <Col sm={12} md={6} lg={4} key={course.id} className="mb-4">
                  <Card
                    className="h-100 course-card"
                    onClick={() => navigate(`/cursos/${course.slug}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="course-card__image-wrapper">
                      {course.cover_image ? (
                        <Card.Img
                          variant="top"
                          src={course.cover_image}
                          alt={course.title}
                          className="course-card__image"
                        />
                      ) : (
                        <div className="course-card__image-placeholder">
                          <span>{course.title?.charAt(0) ?? 'C'}</span>
                        </div>
                      )}
                      <Badge bg={modality.variant} className="course-card__modality-badge">
                        {modality.label}
                      </Badge>
                    </div>
                    <Card.Body className="d-flex flex-column">
                      <Card.Title className="course-card__title">{course.title}</Card.Title>
                      {course.instructor_name && (
                        <p className="course-card__instructor text-muted mb-2">
                          Instructor: {course.instructor_name}
                        </p>
                      )}
                      {course.start_date && (
                        <p className="course-card__date text-muted mb-2" style={{ fontSize: '0.875rem' }}>
                          Inicio:{' '}
                          {new Date(course.start_date).toLocaleDateString('es-DO', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </p>
                      )}
                      <div className="mt-auto pt-2">
                        <span className="course-card__price">
                          {formatPrice(course.price)}
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
              <Button
                variant="outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                ← Anterior
              </Button>
              <span className="text-muted">
                Pagina {currentPage} de {totalPages}
              </span>
              <Button
                variant="outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Siguiente →
              </Button>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default CourseList;
